import React, { useState } from "react";
import PropTypes from "prop-types";
import { DataGrid } from "@mui/x-data-grid";
import MDBox from "components/MDBox";
import { IconButton, Chip, Tooltip } from "@mui/material";
import { Edit, Delete, PriorityHigh } from "components/icons";

const getSeverityColor = (severity) => {
  const colors = {
    LOW: "success",
    MEDIUM: "info",
    HIGH: "warning",
    CRITICAL: "error",
  };
  return colors[severity] || "default";
};

const getStatusColor = (status) => {
  const colors = {
    IDENTIFIED: "info",
    ASSESSED: "warning",
    MITIGATED: "success",
    CLOSED: "default",
    ACCEPTED: "secondary",
  };
  return colors[status] || "default";
};

const getRiskScore = (likelihood, impact) => {
  const likelihoodValues = { LOW: 1, MEDIUM: 2, HIGH: 3, CRITICAL: 4 };
  const impactValues = { LOW: 1, MEDIUM: 2, HIGH: 3, CRITICAL: 4 };
  return (likelihoodValues[likelihood] || 1) * (impactValues[impact] || 1);
};

export const RiskDataGrid = ({ risks, isLoading, onEdit, onDelete, showActions = true }) => {
  const [pageSize, setPageSize] = useState(10);

  const columns = [
    ...(showActions ? [{
      field: "actions",
      type: "actions",
      headerName: "Actions",
      width: 100,
      getActions: (params) => [
        <IconButton key="edit" onClick={() => onEdit(params.row)} color="primary" size="small">
          <Edit />
        </IconButton>,
        <IconButton key="delete" onClick={() => onDelete(params.row)} color="error" size="small">
          <Delete />
        </IconButton>,
      ],
    }] : []),
    { field: "id", headerName: "ID", width: 80 },
    { field: "riskCode", headerName: "Code", width: 120 },
    { field: "riskTitle", headerName: "Risk Title", width: 200, flex: 1 },
    { field: "category", headerName: "Category", width: 120 },
    {
      field: "likelihood",
      headerName: "Likelihood",
      width: 110,
      renderCell: (params) => (
        <Chip label={params.value} color={getSeverityColor(params.value)} size="small" />
      ),
    },
    {
      field: "impact",
      headerName: "Impact",
      width: 100,
      renderCell: (params) => (
        <Chip label={params.value} color={getSeverityColor(params.value)} size="small" />
      ),
    },
    {
      field: "riskScore",
      headerName: "Score",
      width: 80,
      valueGetter: (value, row) => getRiskScore(row.likelihood, row.impact),
      renderCell: (params) => {
        const score = params.value;
        let color = "success";
        if (score >= 9) color = "error";
        else if (score >= 6) color = "warning";
        else if (score >= 3) color = "info";
        return (
          <Tooltip title="Risk Score = Likelihood × Impact">
            <Chip
              icon={score >= 9 ? <PriorityHigh /> : null}
              label={score}
              color={color}
              size="small"
            />
          </Tooltip>
        );
      },
    },
    { field: "owner", headerName: "Owner", width: 130 },
    {
      field: "status",
      headerName: "Status",
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value}
          color={getStatusColor(params.value)}
          size="small"
        />
      ),
    },
  ];

  return (
    <MDBox pt={3} py={3} px={2} height={400}>
      <DataGrid
        rows={risks || []}
        columns={columns}
        pageSize={pageSize}
        onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
        rowsPerPageOptions={[5, 10, 20, 50]}
        loading={isLoading}
        disableSelectionOnClick
        getRowId={(row) => row.id || row.riskId}
      />
    </MDBox>
  );
};

RiskDataGrid.propTypes = {
  risks: PropTypes.array.isRequired,
  isLoading: PropTypes.bool,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
  showActions: PropTypes.bool,
};

export default RiskDataGrid;

